import { defineStore } from 'pinia';
import { IProfileStore } from '@/interfaces/profile/profile-store.interface';
import { BASE_URL } from '@/utils/constants/api-constants';
import axios from 'axios';
import { i18n } from '@/plugins/i18n';
import { DocumentForm } from '@/interfaces/profile/profile-response.interface';
import { deleteData, getData, postData, putData } from '@/services/axios-service';
import {
  IGeneralResponse,
  IPhotoResponseData,
  ResponsePlaceLocation,
  RemoveAccount,
} from '@/interfaces/general-response.interface';
import { ISettings, User } from '@/interfaces/auth/auth-store.interface';
import { IDocument } from '@/interfaces/profile/profile-document.interface';
import { ISettingResponse } from '@/interfaces/setting/setting-response.interface';
import { PlaceLocation } from '@/interfaces/elastic/elastic.interface';

const INITIAL_STATE: IProfileStore = {
  profileForm: {
    name: '',
    lastName: '',
    documentType: undefined,
    documentId: undefined,
    phoneNumber: '',
    prefixPhone: undefined,
    genre: '',
    birthday: undefined,
    userType: undefined,
    NationalityId: 0,
    location: undefined,
  },
  hasProfile: false,
  documentForm: {
    numberId: '',
    renewalDate: '',
    expirationDate: undefined,
  },
  documentSelected: undefined,
  documents: [],
  toggleEditProfile: false,
  toggleChangeEmail: false,
  toggleChangePassword: false,
  toggleRecoveryPassword: false,
  toggleDocument: false,
  languageSelected: 'en',
  pushNotifications: true,
};

export const useProfileStore = defineStore({
  id: 'profile',
  state: (): IProfileStore => ({
    ...INITIAL_STATE,
  }),
  getters: {
    isProfileCompleted(state) {
      return (
        state.profileForm.name !== undefined &&
        state.profileForm.lastName !== undefined &&
        state.profileForm.documentType !== undefined &&
        state.profileForm.documentId !== undefined &&
        state.profileForm.phoneNumber !== undefined &&
        state.profileForm.prefixPhone !== undefined &&
        state.profileForm.genre !== undefined &&
        state.profileForm.birthday !== undefined &&
        state.profileForm.NationalityId !== 0
      );
    },
  },
  actions: {
    async updatePhoto(base64: string) {
      try {
        const { data, error, status } = await postData<IPhotoResponseData>(
          `${BASE_URL.USERS}profile/photo`,
          {
            photo: base64,
          },
        );
        if (status == axios.HttpStatusCode.Ok && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async sendCode(email: string) {
      try {
        const { msg, error, status } = await postData<IGeneralResponse>(
          `${BASE_URL.USERS}profile/change-email/send-code`,
          {
            newEmail: email,
          },
        );
        if (msg && status == axios.HttpStatusCode.Ok) {
          return msg;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async verifyCode(email: string, code: string) {
      try {
        const { data, error, status } = await postData<IGeneralResponse>(
          `${BASE_URL.USERS}profile/change-email/check-code`,
          {
            newEmail: email,
            code: code,
          },
        );
        if (status == axios.HttpStatusCode.Ok && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async changePassword(currentPassword: string, newPassword: string) {
      try {
        const { msg, error, status } = await putData<IGeneralResponse>(
          `${BASE_URL.USERS}profile/change-password`,
          {
            currentPassword: currentPassword,
            newPassword: newPassword,
          },
        );
        if (msg && status == axios.HttpStatusCode.Ok) {
          return msg;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async changeCurrentPassword(newPassword: string) {
      try {
        const { msg, error, status } = await putData<IGeneralResponse>(
          `${BASE_URL.USERS}profile/change-password`,
          {
            newPassword: newPassword,
          },
        );
        if (msg && status == axios.HttpStatusCode.Ok) {
          return msg;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async recoveryPassword(email: string) {
      try {
        const { msg, error, status } = await postData<IGeneralResponse>(
          `${BASE_URL.USERS}recovery`,
          {
            email: email,
          },
        );
        if (msg && status == axios.HttpStatusCode.Ok) {
          return msg;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async resetPassword(password: string, token: string) {
      try {
        const { msg, error, status } = await postData<IGeneralResponse>(
          `${BASE_URL.USERS}recovery/reset-password`,
          {
            password: password,
            token: token,
          },
        );
        if (msg && status == axios.HttpStatusCode.Ok) {
          return msg;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async getProfile() {
      try {
        const { data, error, status } = await getData<User>(`${BASE_URL.USERS}profile`);
        if (data && status == axios.HttpStatusCode.Ok) {
          this.hasProfile =
            data.documentId !== null &&
            data.phoneNumber !== null &&
            data.prefixPhone !== null &&
            data.genre !== null &&
            data.birthday !== null &&
            data.Nationality?.id !== 0;

          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async getProfileLocation() {
      try {
        const { data, error, status } = await getData<ResponsePlaceLocation>(
          `${BASE_URL.USERS}profile/location`,
        );
        if (data && status == axios.HttpStatusCode.Ok) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async updateProfileLocation(location: PlaceLocation) {
      try {
        const { data, error, status } = await putData<ResponsePlaceLocation>(
          `${BASE_URL.USERS}profile/location`,
          location,
        );
        if (data && status == axios.HttpStatusCode.Ok) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async updateProfile() {
      try {
        const { data, error, status } = await putData<User>(`${BASE_URL.USERS}profile`, {
          name: this.profileForm.name,
          lastName: this.profileForm.lastName,
          genre: this.profileForm.genre,
          NationalityId: +Number(this.profileForm?.NationalityId),
          prefixPhone: this.profileForm.prefixPhone,
          phoneNumber: this.profileForm.phoneNumber,
          documentId: this.profileForm.documentId,
          documentType: this.profileForm.documentType,
          birthday: this.profileForm.birthday,
          userType: this.profileForm.userType,
          Location: this.profileForm.Location,
        });
        if (data && status == axios.HttpStatusCode.Ok) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async getDocuments() {
      try {
        const { data, error, status } = await getData<IDocument[]>(
          `${BASE_URL.USERS}profile/documents`,
        );
        if (data && status == axios.HttpStatusCode.Ok) {
          this.documents = data;
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async saveDocuments(form: DocumentForm) {
      try {
        const res = await postData<[]>(`${BASE_URL.USERS}profile/documents`, {
          ...form,
        });
        const { success, data, error } = res;
        if (success && data) {
          return res;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async removeAccount(password: string) {
      try {
        const res = await postData<RemoveAccount>(`${BASE_URL.USERS}profile/delete-account`, {
          password,
        });
        const { success, error } = res;
        if (success) {
          return res;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async deleteAnswerInput(id: string) {
      try {
        const res = await deleteData<IDocument[]>(
          `${BASE_URL.USERS}profile/documents/answer/${id}`,
        );
        const { success, error } = res;
        if (success) {
          return res;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getSetting() {
      try {
        const { data, error, status } = await getData<ISettings>(
          `${BASE_URL.USERS}profile/settings`,
        );
        if (data && status == axios.HttpStatusCode.Ok) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async updateSetting(language: string, pushNotifications: boolean) {
      try {
        const params = {
          language: language,
          pushNotifications: pushNotifications,
        };
        const response = await putData<ISettingResponse>(
          `${BASE_URL.USERS}profile/settings`,
          params,
        );
        if (response.success) {
          return response;
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },

    resetStore() {
      this.profileForm = {
        name: '',
        lastName: '',
        documentType: undefined,
        documentId: undefined,
        phoneNumber: '',
        prefixPhone: '',
        genre: '',
        birthday: undefined,
        userType: undefined,
        NationalityId: 0,
        location: undefined,
      };
      this.hasProfile = false;
      this.documentForm = {
        numberId: '',
        renewalDate: '',
        expirationDate: undefined,
      };
      this.documents = [];
      this.documentSelected = undefined;
      this.toggleEditProfile = false;
      this.toggleChangeEmail = false;
      this.toggleChangePassword = false;
      this.toggleRecoveryPassword = false;
      this.toggleDocument = false;
      this.languageSelected = 'en';
    },
  },
  persist: {},
});

export type ProfileStore = ReturnType<typeof useProfileStore>;
